 import React, { useState, useRef, useEffect } from 'react';
import {
  Button, Typography, Card, Input, Space, Avatar, Row, Col, Tag, Spin, message
} from 'antd';
import {
  ArrowLeftOutlined, SendOutlined, CustomerServiceOutlined,
  TagOutlined, PercentageOutlined, AudioOutlined, SoundOutlined,
  ShoppingCartOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const AIAssistant = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hey there! 👋 I'm Dollar Furniture AI, your personal furniture shopping assistant. I can help you find products, plan a budget, and even let you shop directly through chat!\n\nYou can talk to me like a friend. Try:\n• 'I have 100,000 and need a bed, sofa, and dining table'\n• 'Show me sofas under 50000'\n• 'Add the Mabawa recliner to my cart'\n• 'Show me my cart'\n• 'I want to place an order'\n• 'What's your return policy?'\n\nReady? Tell me what you're looking for! 😊",
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      products: null,
      component: null,
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const chatContainerRef = useRef(null);
  const [allProductsCache, setAllProductsCache] = useState([]);
  
  // Shopping cart state
  const [cartItems, setCartItems] = useState([]);
  const [orderStep, setOrderStep] = useState(null);
  const [deliveryDetails, setDeliveryDetails] = useState({ name: '', phone: '', address: '' });
  const [currentDetailField, setCurrentDetailField] = useState(null);

  // Conversation memory
  const [lastBudgetContext, setLastBudgetContext] = useState(null);
  const [lastPlan, setLastPlan] = useState(null);
  const [pendingAddCategory, setPendingAddCategory] = useState(null);
  const [awaitingBudgetForCategory, setAwaitingBudgetForCategory] = useState(null);
  const [pendingAddToPlan, setPendingAddToPlan] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  // Voice output - always enabled, no toggle
  const speechSynth = window.speechSynthesis;
  let currentUtterance = null;

  const speakText = (text) => {
    if (!speechSynth) return;
    if (currentUtterance) speechSynth.cancel();
    let cleanText = text.replace(/[🛋️🛏️💰🔄📍📞🎉📦🛡️💳🔧🎨🌳📏🧼❌🏢🎟️📋⭐🆕✏️🏠📱👋😊🤔💡✅❌🎯]/g, '');
    cleanText = cleanText.replace(/\*/g, '').replace(/[•✅❌]/g, '').replace(/\n/g, '. ');
    try {
      const utterance = new SpeechSynthesisUtterance(cleanText);
      // Wait for voices to be loaded (fix for some browsers)
      if (speechSynth.getVoices().length === 0) {
        speechSynth.onvoiceschanged = () => {
          setVoiceAndSpeak(utterance);
        };
      } else {
        setVoiceAndSpeak(utterance);
      }
    } catch (e) {
      console.warn("Speech synthesis error:", e);
    }
  };

  const setVoiceAndSpeak = (utterance) => {
    const voices = speechSynth.getVoices();
    const femaleVoice = voices.find(voice => 
      voice.name.includes('Google UK English Female') || 
      voice.name.includes('Samantha') || 
      voice.name.includes('Google US English Female') ||
      voice.gender === 'female'
    );
    if (femaleVoice) utterance.voice = femaleVoice;
    utterance.rate = 0.9;
    utterance.pitch = 1.1;
    utterance.lang = 'en-US';
    currentUtterance = utterance;
    speechSynth.speak(utterance);
  };

  // Preload voices
  useEffect(() => {
    if (speechSynth) {
      speechSynth.getVoices(); // trigger load
    }
  }, []);

  // Company info (unchanged)
  const COMPANY_INFO = {
    returnPolicy: "🔄 **Return Policy**\n\nWe want you to love your furniture! If you're not completely satisfied:\n• **14-day return window** from delivery date\n• Items must be unused and in original packaging\n• Customer is responsible for return shipping unless item is defective\n• Refunds processed within 7 business days after inspection\n• Custom-made items are non-refundable (but we'll help with modifications)\n\nFor returns, please contact our support team.",
    whatsapp: "+254729335354",
    phone: "+254729335354",
    email: "mwauraj118@gmail.com",
    showroom: "📍 **Showroom Address**\nKamakis Bypass, Ruiru - Nairobi, Kenya\n\n**Hours:** Monday - Sunday, 24/7 (by appointment for late visits)",
    supportHours: "24 Hours, 7 Days a Week – We're always here to help!",
    deliveryInfo: "🚚 **Delivery Information**\n\nWe deliver across Kenya. Delivery fees are calculated based on your location and order size. Contact us for a quote – we'll give you the best rate possible! For urgent deliveries or large orders, please reach out via WhatsApp for a custom arrangement.",
    warranty: "🛡️ **Warranty**\n\n• 2-year limited warranty on all non-electrical furniture\n• Covers manufacturing defects, structural issues, and material defects\n• Does not cover normal wear and tear, stains, or damage from misuse\n• To claim, contact support with your order number and photos/video of the issue",
    paymentMethods: "💳 **Payment Methods**\n\nWe accept:\n• Cash on delivery (COD)\n• M-Pesa (Paybill number will be provided)\n• Bank transfer (details on request)\n• Credit/Debit cards via our website (coming soon)\n• Installments / layaway – ask for details!",
    assembly: "🔧 **Assembly Services**\n\n• Most items come with self-assembly instructions (tools not included)\n• Professional assembly available for Ksh 2,000–5,000 per item (depending on complexity)\n• White-glove delivery (carry in, assemble, remove packaging) available upon request",
    customization: "🎨 **Custom Furniture**\n\nYes! We offer custom-made furniture:\n• Choose your size, material, finish, and color\n• Lead time: 2–4 weeks\n• Price quoted per project\n• Contact us on WhatsApp to start your custom order",
    materialInfo: "🌳 **Materials**\n\nOur furniture uses:\n• Solid wood (oak, pine, mahogany, teak, rubberwood)\n• Engineered wood (MDF, plywood) with premium veneers\n• High-density foam cushions\n• Fabric, velvet, leatherette, and genuine leather upholstery\n• Metal frames for sofas and bed frames\n• Glass tabletops (tempered)\n\nAsk for specific product materials!",
    dimensions: "📏 **Dimensions**\n\nProduct dimensions are listed on each product page. If you need exact measurements for a specific item, just ask! I can look it up.",
    careInstructions: "🧼 **Care Instructions**\n\n• Fabric: Vacuum regularly, spot clean with mild detergent\n• Leather: Wipe with damp cloth, use leather conditioner every 6 months\n• Wood: Dust with soft cloth, avoid direct sunlight and moisture\n• Glass: Use glass cleaner and soft cloth\n• For deep stains or damage, contact our support team",
    deliveryTracking: "📦 **Delivery Tracking**\n\nOnce your order ships, you'll receive a tracking link via SMS/WhatsApp. You can also contact our support with your order number to check status.",
    cancelPolicy: "❌ **Cancellation & Modification**\n\n• Free cancellation within 24 hours of order\n• After 24 hours, a 10% restocking fee applies (if item not yet shipped)\n• Once shipped, returns follow the 14-day return policy\n• Custom orders cannot be cancelled once production starts",
    financing: "💰 **Financing / Installments**\n\nWe offer flexible payment plans through M-Pesa (0% interest for 3 months on selected items) and external financing partners. Ask about 'lipa pole pole' options – just contact our support team.",
    tradeIn: "🔄 **Trade‑In / Old Furniture Removal**\n\nWe currently do not have a trade‑in program, but we can recommend local charities that accept furniture donations. We can also remove your old furniture during delivery for a small fee (Ksh 1,500–3,000).",
    bulkOrder: "🏢 **Bulk & Commercial Orders**\n\nDiscounts available for hotels, offices, schools, and housing projects. Please contact our B2B team via WhatsApp – we'll provide a tailored quote.",
    discounts: "🎟️ **Discounts & Promotions**\n\n• Student/Military/Senior: 5% off (valid ID required)\n• First purchase: 10% off (sign up for newsletter)\n• Refer a friend: Get Ksh 2,000 off your next order\n• Seasonal sales (up to 30%) – ask about current offers!",
    productInfo: "📋 **Product Information**\n\nI can tell you about specifications, sizes, materials, colors, and prices. Just ask me for a specific product name, or browse categories.",
    bestSellers: "⭐ **Best‑Sellers**\n\nOur most popular items:\n• The Dollar Furniture Bed (various sizes)\n• 3+2 Seater Sofa Sets\n• 5x6 Standard Bed\n• Recliner chairs\n• TV stands\n• Coffee tables\n• Dining sets\n\nWould you like to see any of these?",
    newArrivals: "🆕 **New Arrivals**\n\nWe regularly add new designs! Check our products page or ask me for 'new beds', 'new sofas', etc. I'll show you the latest additions.",
    customOrder: "✏️ **Custom Order Process**\n\n1. Contact us on WhatsApp\n2. Share your design, dimensions, and material preference\n3. We provide a quote and timeline (2–4 weeks)\n4. 50% deposit to start production\n5. Balance on delivery\n\nCustom items are non-refundable but we'll work with you to get it right.",
    showroomVisit: "🏠 **Visit Our Showroom**\n\n📍 Kamakis Bypass, Ruiru - Nairobi, Kenya\n\nOpen 24/7 (but please call ahead for appointment). We have over 100 pieces on display – you can touch, sit, and test quality before buying!",
    contactSupport: "📱 **Contact Support**\n\n• WhatsApp: +254729335354 (fastest)\n• Phone: +254729335354\n• Email: mwauraj118@gmail.com\n• In‑person at showroom\n\nWe respond within minutes on WhatsApp!",
  };

  const WHATSAPP_NUMBER = COMPANY_INFO.whatsapp;
  const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`;

  const suggestions = [
    { text: "🛋️ I need a sofa", value: "I need a sofa" },
    { text: "🛏️ Show me beds", value: "Show me beds" },
    { text: "💰 Budget 100,000 for bed and sofa", value: "I have 100,000 and need a bed and sofa" },
    { text: "🛒 Add to cart", value: "Add The Dollar Furniture Bed to my cart" },
    { text: "🛍️ Show my cart", value: "Show my cart" },
    { text: "📦 Place order", value: "I want to place an order" },
    { text: "🔄 Return policy", value: "What's your return policy?" },
    { text: "📍 Showroom location", value: "Where is your showroom?" },
    { text: "📞 Contact support", value: "How can I contact you?" },
    { text: "🎉 Offers", value: "What offers do you have?" },
    { text: "🤖 How to access this AI?", value: "How to get to miss dollar?" },
  ];

  // Voice recognition
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = SpeechRecognition ? new SpeechRecognition() : null;
  if (recognition) {
    recognition.continuous = false;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
  }

  const startListening = () => {
    if (!recognition) {
      message.error("Voice input not supported in this browser. Try Chrome or Edge.");
      return;
    }
    setIsListening(true);
    recognition.start();
    recognition.onresult = (event) => {
      const speechText = event.results[0][0].transcript;
      setInputValue(speechText);
      setIsListening(false);
      setTimeout(() => {
        if (speechText.trim()) sendMessage(speechText);
      }, 300);
    };
    recognition.onerror = (event) => {
      console.error("Speech error:", event.error);
      setIsListening(false);
      message.error("Could not hear you. Please try again or type.");
    };
    recognition.onend = () => {
      setIsListening(false);
    };
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const getActualPrice = (product) => {
    const price = product.price || 0;
    const discount = product.discount || 0;
    return price - (price * discount) / 100;
  };

  const fetchAllProducts = async () => {
    if (allProductsCache.length > 0) return allProductsCache;
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllProductsCache(products);
      return products;
    } catch (err) {
      console.error("Error fetching products:", err);
      return [];
    }
  };

  const normalizeCategory = (term) => {
    let t = term.toLowerCase().trim();
    t = t.replace(/^(a|an|the)\s+/, '');
    t = t.replace(/s$/, '');
    const map = {
      'recliner': 'recliner',
      'sofa': 'sofa',
      'couch': 'sofa',
      'bed': 'bed',
      'table': 'table',
      'dining table': 'dining table',
      'coffee table': 'coffee table',
      'tv stand': 'tv stand',
      'tv': 'tv stand',
      'wardrobe': 'wardrobe',
      'cabinet': 'cabinet',
      'desk': 'desk',
      'chair': 'chair',
      'lamp': 'lamp',
      'bubble bed': 'bed',
    };
    return map[t] || t;
  };

  const matchCategory = (userTerm, productCategory, productTitle) => {
    if (!productCategory && !productTitle) return false;
    const normUser = normalizeCategory(userTerm);
    const normCat = productCategory ? productCategory.toLowerCase().replace(/s$/, '') : '';
    const normTitle = productTitle ? productTitle.toLowerCase() : '';
    
    if (normCat === normUser) return true;
    if (normTitle.includes(normUser)) return true;
    
    if (normUser === 'recliner' && (normCat.includes('recliner') || normTitle.includes('recliner'))) return true;
    if (normUser === 'dining table' && (normCat.includes('dining') && normCat.includes('table'))) return true;
    if (normUser === 'coffee table' && (normCat.includes('coffee') && normCat.includes('table'))) return true;
    if (normUser === 'sofa' && (normCat.includes('sofa') || normCat === 'couch')) return true;
    if (normCat.includes(normUser) && normUser.length > 3) return true;
    return false;
  };

  const searchProductsByTerm = (term, allProducts) => {
    const norm = normalizeCategory(term);
    return allProducts.filter(p => matchCategory(norm, p.category, p.title));
  };

  const getCheapestProductForCategory = (categoryName, allProducts) => {
    const matchingProducts = searchProductsByTerm(categoryName, allProducts);
    if (matchingProducts.length === 0) return null;
    return matchingProducts.reduce((a, b) => getActualPrice(a) < getActualPrice(b) ? a : b);
  };

  const suggestAffordableCombination = (totalBudget, requestedCategories, allProducts) => {
    const categoryItems = [];
    for (let cat of requestedCategories) {
      const cheapest = getCheapestProductForCategory(cat, allProducts);
      if (cheapest) {
        categoryItems.push({ category: cat, product: cheapest, price: getActualPrice(cheapest) });
      } else {
        categoryItems.push({ category: cat, product: null, price: Infinity });
      }
    }
    
    const sorted = [...categoryItems].sort((a, b) => a.price - b.price);
    let remaining = totalBudget;
    const affordable = [];
    const missing = [];
    
    for (let item of sorted) {
      if (item.product === null) {
        missing.push({ category: item.category, reason: 'no_product' });
        continue;
      }
      if (item.price <= remaining) {
        affordable.push(item);
        remaining -= item.price;
      } else {
        missing.push({ category: item.category, cheapestProduct: item.product, price: item.price, reason: 'budget' });
      }
    }
    
    const orderedAffordable = requestedCategories
      .map(cat => affordable.find(a => a.category === cat))
      .filter(a => a !== undefined);
    
    const missingWithPrice = missing.filter(m => m.reason === 'budget');
    const cheapestMissing = missingWithPrice.length > 0 ? missingWithPrice[0] : null;
    const additionalNeeded = cheapestMissing ? cheapestMissing.price - remaining : 0;
    
    return {
      affordableProducts: orderedAffordable.map(a => a.product),
      remainingBudget: remaining,
      missingCategories: missing.map(m => m.category),
      cheapestMissingProduct: cheapestMissing ? cheapestMissing.cheapestProduct : null,
      additionalNeeded: Math.max(0, additionalNeeded),
      totalUsed: totalBudget - remaining,
      missingWithPrice,
    };
  };

  const storeBudgetPlan = (budget, categories, plan) => {
    setLastBudgetContext({ budget, categories });
    setLastPlan({ ...plan, requestedCategories: categories, totalBudget: budget });
    setShowSuggestions(false);
  };

  const humanGreeting = () => {
    const greetings = [
      "Hey! 😊 How can I help with your furniture hunt today?",
      "Hello again! Ready to find some awesome pieces?",
      "Hi there! I'm all ears – what are you looking for?",
      "Hey friend! Need help with a budget or a specific item?",
      "Good to see you! What furniture are you looking for today?",
      "Welcome back! How can I assist you with your home makeover?",
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  };

  // Access guide (unchanged)
  const getAccessGuide = () => {
    const stepByStepHTML = `
      <!DOCTYPE html>
      <html>
      <head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Segoe UI',system-ui;background:#f8f9fa;padding:16px;line-height:1.4}
        .step{background:white;border-radius:16px;margin-bottom:12px;padding:12px;box-shadow:0 2px 8px rgba(0,0,0,0.05);display:flex;align-items:center;gap:12px}
        .step-number{width:36px;height:36px;background:#1890ff;color:white;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;flex-shrink:0}
        .step-content{flex:1}
        .step-title{font-weight:600;margin-bottom:4px}
        .step-desc{font-size:13px;color:#555}
        .icon{font-size:20px}
        hr{margin:12px 0;border-color:#e0e0e0}
        .highlight{background:#fff7e6;border-left:4px solid #ffc53d}
      </style></head>
      <body>
        <div class="step"><div class="step-number">1</div><div class="step-content"><div class="step-title">🌐 Open your browser</div><div class="step-desc">Chrome, Safari, Firefox – any browser works!</div></div></div>
        <div class="step"><div class="step-number">2</div><div class="step-content"><div class="step-title">🔍 Search "dollar furniture"</div><div class="step-desc">Use Google or any search engine. First result will be <strong>dollarfurnitureke.com</strong></div></div></div>
        <div class="step"><div class="step-number">3</div><div class="step-content"><div class="step-title">🖱️ Click the official site</div><div class="step-desc">Make sure the domain is <strong>dollarfurnitureke.com</strong></div></div></div>
        <div class="step"><div class="step-number">4</div><div class="step-content"><div class="step-title">👆 On the home page, look at the <strong>right side</strong></div><div class="step-desc">You will see a floating "Dollar Furniture AI" icon (chat bubble).</div></div></div>
        <div class="step"><div class="step-number">5</div><div class="step-content"><div class="step-title">💬 Click the icon</div><div class="step-desc">It opens the chat window – that's me! 🎉</div></div></div>
        <div class="step highlight"><div class="step-number">✨</div><div class="step-content"><div class="step-title">Wooolaa! You've found me!</div><div class="step-desc">Now I can help you shop, create a budget, find the best deals, and even take your order.</div></div></div>
        <hr />
        <div style="font-size:12px;color:#999;text-align:center">Still stuck? <a href="#" onclick="window.parent.postMessage('whatsapp','*')">📞 WhatsApp support</a></div>
      </body>
      </html>
    `;
    const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(stepByStepHTML)}`;
    return (
      <iframe
        src={dataUrl}
        title="How to access Dollar Furniture AI"
        style={{ width: '100%', height: '420px', border: 'none', borderRadius: '16px', marginTop: '12px' }}
        sandbox="allow-same-origin allow-scripts allow-popups allow-modals"
      />
    );
  };

  // ==================== SHOPPING CART FUNCTIONS ====================
  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity }];
    });
    message.success(`${product.title} added to cart!`);
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.product.id !== productId));
    message.info('Item removed from cart');
  };

  const getCartTotal = () => {
    return cartItems.reduce((sum, item) => sum + getActualPrice(item.product) * item.quantity, 0);
  };

  const startOrderProcess = () => {
    if (cartItems.length === 0) {
      return "Your cart is empty. Add some items first!";
    }
    setOrderStep('collecting_details');
    setCurrentDetailField('name');
    return "Great! Let's place your order. Please tell me your **full name**.";
  };

  const processDeliveryInput = (input, currentDeliveryDetails, currentCartItems, setDeliveryDetailsFn, setCurrentDetailFieldFn, setOrderStepFn, setCartItemsFn, getCartTotalFn, getActualPriceFn, whatsappNumber) => {
    const field = currentDetailField;
    if (field === 'name') {
      setDeliveryDetailsFn(prev => ({ ...prev, name: input }));
      setCurrentDetailFieldFn('phone');
      return "Thanks! Now, please share your **phone number** (e.g., 0712345678).";
    }
    if (field === 'phone') {
      setDeliveryDetailsFn(prev => ({ ...prev, phone: input }));
      setCurrentDetailFieldFn('address');
      return "Almost done! Please provide your **delivery address** (street, estate, landmark).";
    }
    if (field === 'address') {
      const finalDetails = { ...currentDeliveryDetails, address: input };
      const total = getCartTotalFn();
      const orderItems = currentCartItems.map(item => 
        `• ${item.product.title} × ${item.quantity} = Ksh ${(getActualPriceFn(item.product) * item.quantity).toFixed(2)}`
      ).join('\n');
      const customerDetails = `Name: ${finalDetails.name}\nPhone: ${finalDetails.phone}\nAddress: ${finalDetails.address}`;
      const message = `🛍️ **NEW ORDER**\n\n${orderItems}\n\n**Total:** Ksh ${total.toFixed(2)}\n\n**Customer Details:**\n${customerDetails}\n\nPlease confirm and process this order.`;
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
      
      setOrderStepFn(null);
      setCurrentDetailFieldFn(null);
      setDeliveryDetailsFn({ name: '', phone: '', address: '' });
      setCartItemsFn([]);
      
      return {
        text: `✅ **Order placed successfully!**\n\nClick the link below to send your order to our WhatsApp team:\n\n[📦 Complete Order on WhatsApp](${whatsappUrl})\n\nWe'll confirm delivery details shortly. Thank you for shopping with Dollar Furniture! 🎉`,
        isWhatsappLink: true
      };
    }
    return null;
  };

  // ==================== INTENT HANDLERS ====================
  const handleIntent = async (lowerText, allProducts) => {
    // Access guide
    if (
      lowerText.match(/how (to|can i) (get|access|find|reach|use|open|start) (to )?(dollar furniture|the assistant|the chatbot|you|the ai)/i) ||
      lowerText.match(/how (do i) (get|access|find|reach|use|open|start) (to )?(dollar furniture|the assistant|the chatbot|you|the ai)/i) ||
      lowerText.match(/how to get to miss dollar/i) ||
      lowerText.match(/how to (find|open|use|reach) (dollar furniture)/i) ||
      lowerText.match(/where can i find (the chatbot|the ai assistant)/i) ||
      lowerText.match(/open (dollar furniture|the assistant)/i)
    ) {
      return {
        response: "✨ **How to access Dollar Furniture AI**\n\n1. Open your browser (Chrome, Safari, Firefox).\n2. Search **\"dollar furniture\"** on Google.\n3. Click the first result – the domain is **dollarfurnitureke.com**.\n4. On the home page, **look at the right side** – you'll see a floating icon of me (Dollar Furniture AI).\n5. **Click that icon** – the chat window opens and... **Wooolaa!** 🎉 You're now talking to me!\n\nFrom here I can help you shop, create a budget, find the best deals, and even place your order. 😊\n\n👇 **See the step‑by‑step visual guide below** 👇",
        products: null,
        component: getAccessGuide()
      };
    }

    // Cart & Order intents
    if (lowerText.match(/show (my )?cart|view cart|what'?s in my cart/i)) {
      if (cartItems.length === 0) {
        return { response: "🛒 Your cart is empty. Use 'add [product] to my cart' to start shopping!", products: null, component: null };
      }
      const cartSummary = cartItems.map(item => 
        `• ${item.product.title} – Ksh ${getActualPrice(item.product).toFixed(2)} × ${item.quantity} = Ksh ${(getActualPrice(item.product) * item.quantity).toFixed(2)}`
      ).join('\n');
      const total = getCartTotal();
      return {
        response: `🛒 **Your Cart**\n\n${cartSummary}\n\n**Total: Ksh ${total.toFixed(2)}**\n\nTo remove an item, say "remove [product name]". To place order, say "place order".`,
        products: null,
        component: null
      };
    }

    if (lowerText.match(/place (my )?order|checkout|buy now|order now/i)) {
      const startMsg = startOrderProcess();
      return { response: startMsg, products: null, component: null };
    }

    if (lowerText.match(/remove (.*?) from (my )?cart/i)) {
      const match = lowerText.match(/remove (.*?) from (my )?cart/i);
      if (match) {
        const productName = match[1].trim();
        const foundItem = cartItems.find(item => item.product.title.toLowerCase().includes(productName));
        if (foundItem) {
          removeFromCart(foundItem.product.id);
          return { response: `Removed ${foundItem.product.title} from your cart.`, products: null, component: null };
        } else {
          return { response: `Could not find "${productName}" in your cart.`, products: null, component: null };
        }
      }
    }

    if (lowerText.match(/add (.*?) to (my )?cart/i)) {
      const match = lowerText.match(/add (.*?) to (my )?cart/i);
      if (match) {
        const productName = match[1].trim();
        const allProds = allProductsCache.length ? allProductsCache : await fetchAllProducts();
        const matchedProducts = searchProductsByTerm(productName, allProds);
        if (matchedProducts.length === 0) {
          return { response: `Sorry, I couldn't find a product matching "${productName}". Try a more specific name.`, products: null, component: null };
        }
        const product = matchedProducts[0];
        addToCart(product, 1);
        return { response: `✅ Added ${product.title} to your cart! You now have ${cartItems.length + 1} item(s). Say "show my cart" to review.`, products: null, component: null };
      }
    }

    // Existing company info intents (unchanged)
    if (lowerText.match(/return|policy|refund|exchange/)) {
      return { response: COMPANY_INFO.returnPolicy, products: null, component: null };
    }
    if (lowerText.match(/contact|support|reach|customer service/)) {
      return { response: COMPANY_INFO.contactSupport, products: null, component: null };
    }
    if (lowerText.match(/showroom|location|address|where are you|kamakis/)) {
      return { response: COMPANY_INFO.showroomVisit, products: null, component: null };
    }
    if (lowerText.match(/delivery|shipping|deliver|delivery fee|shipping cost/)) {
      return { response: COMPANY_INFO.deliveryInfo, products: null, component: null };
    }
    if (lowerText.match(/warranty|guarantee|cover|defect/)) {
      return { response: COMPANY_INFO.warranty, products: null, component: null };
    }
    if (lowerText.match(/payment|pay|mpesa|credit|card|installment|layaway|finance/)) {
      return { response: COMPANY_INFO.paymentMethods, products: null, component: null };
    }
    if (lowerText.match(/assembly|assemble|build|put together|install/)) {
      return { response: COMPANY_INFO.assembly, products: null, component: null };
    }
    if (lowerText.match(/custom|design|tailor|made to order|bespoke/)) {
      return { response: COMPANY_INFO.customization, products: null, component: null };
    }
    if (lowerText.match(/material|wood|fabric|leather|foam|upholstery/)) {
      return { response: COMPANY_INFO.materialInfo, products: null, component: null };
    }
    if (lowerText.match(/dimension|size|measure|length|width|height/)) {
      return { response: COMPANY_INFO.dimensions, products: null, component: null };
    }
    if (lowerText.match(/care|maintenance|clean|wash|protect|stain/)) {
      return { response: COMPANY_INFO.careInstructions, products: null, component: null };
    }
    if (lowerText.match(/track|tracking|order status|where is my order/)) {
      return { response: COMPANY_INFO.deliveryTracking, products: null, component: null };
    }
    if (lowerText.match(/cancel|cancellation|modify|change order/)) {
      return { response: COMPANY_INFO.cancelPolicy, products: null, component: null };
    }
    if (lowerText.match(/finance|installment|lipa|pole|polepole|credit|loan/)) {
      return { response: COMPANY_INFO.financing, products: null, component: null };
    }
    if (lowerText.match(/trade.?in|old furniture|remove old|donation/)) {
      return { response: COMPANY_INFO.tradeIn, products: null, component: null };
    }
    if (lowerText.match(/bulk|wholesale|commercial|hotel|office|school|b2b/)) {
      return { response: COMPANY_INFO.bulkOrder, products: null, component: null };
    }
    if (lowerText.match(/discount|offer|sale|promo|coupon|voucher|student|military|senior|first purchase|refer/)) {
      return { response: COMPANY_INFO.discounts, products: null, component: null };
    }
    if (lowerText.match(/best seller|most popular|top rated|bestseller/)) {
      return { response: COMPANY_INFO.bestSellers, products: null, component: null };
    }
    if (lowerText.match(/new arrival|new in|just arrived|latest/)) {
      return { response: COMPANY_INFO.newArrivals, products: null, component: null };
    }
    if (lowerText.match(/spec|specification|details|features|description/)) {
      return { response: COMPANY_INFO.productInfo, products: null, component: null };
    }
    if (lowerText.match(/custom order|how to custom|commission/)) {
      return { response: COMPANY_INFO.customOrder, products: null, component: null };
    }
    if (lowerText.match(/offer|discount|deal|sale|promo/i) && !lowerText.match(/budget/)) {
      const offerProducts = allProducts.filter(p => p.discount && p.discount > 0).slice(0, 8);
      if (offerProducts.length === 0) {
        return { response: "No active discounts right now – but we add new deals every week. Check back soon, or tell me what you're looking for!", products: null, component: null };
      }
      return { response: "🎉 Ooh, sales! Here are our hottest discounted items. Each price shown is after discount:", products: offerProducts, component: null };
    }
    if (lowerText.match(/^(hi|hello|hey|good morning|good afternoon|good evening|howdy|sup)$/)) {
      return { response: humanGreeting(), products: null, component: null };
    }
    if (lowerText.match(/thank|thanks|appreciate|awesome|great/)) {
      const thanks = [
        "You're very welcome! 😊 Happy shopping! Need anything else, just holler.",
        "My pleasure! I'm always here when you need furniture advice. Have a lovely day!",
        "Anytime! If you change your mind or want to see more options, I'm just a message away.",
        "So glad I could help! Come back anytime you need furniture advice.",
      ];
      return { response: thanks[Math.floor(Math.random() * thanks.length)], products: null, component: null };
    }
    if (lowerText.match(/bye|goodbye|see ya|see you/)) {
      return { response: "Goodbye! 🛋️ Come back anytime you need furniture advice. Have a wonderful day! 😊", products: null, component: null };
    }
    if (lowerText.match(/help|what can you do|options|commands/)) {
      return {
        response: "Sure! I can help with:\n• Finding products (e.g., 'show me sofas')\n• Budget planning (e.g., 'I have 100,000 for a bed and sofa')\n• Shopping (add to cart, view cart, place order)\n• Company policies (returns, delivery, warranty)\n• Payments, assembly, custom orders\n• Best sellers and new arrivals\n\nJust ask anything about furniture!",
        products: null, component: null
      };
    }
    if (lowerText.match(/category|categories|list.*type/)) {
      const categoriesSet = new Set();
      allProducts.forEach(p => { if (p.category) categoriesSet.add(p.category); });
      const categories = [...categoriesSet];
      const catList = categories.map(c => `• ${c}`).join('\n');
      return {
        response: `Sure thing! Here's everything we've got:\n${catList}\n\nJust tell me which category you're interested in, and I'll show you the best options.`,
        products: null, component: null
      };
    }
    return null;
  };

  // ==================== Helper functions ====================
  const extractBudgetAndMultipleCategories = (text) => {
    const budgetMatch = text.match(/(?:budget|have|i have|my budget is|for)\s*(\d{4,})/i);
    if (!budgetMatch) return null;
    const budget = parseFloat(budgetMatch[1]);
    let afterBudget = text.substring(budgetMatch.index + budgetMatch[0].length);
    if (!afterBudget.trim() && budgetMatch.index > 0) {
      afterBudget = text.substring(0, budgetMatch.index);
    }
    const categoryKeywords = ['bed', 'sofa', 'table', 'dining table', 'coffee table', 'tv stand', 'wardrobe', 'cabinet', 'desk', 'chair', 'lamp', 'recliner', 'couch', 'mattress', 'nightstand', 'side table', 'dining set', 'sofa set'];
    let foundCategories = [];
    const parts = afterBudget.split(/[,&]|\band\b/i);
    for (let part of parts) {
      part = part.trim().toLowerCase();
      part = part.replace(/^(a|an|need|want|for|i need|i want)\s+/i, '');
      for (let cat of categoryKeywords) {
        if (part.includes(cat)) {
          foundCategories.push(normalizeCategory(cat));
          break;
        }
      }
    }
    foundCategories = [...new Set(foundCategories)];
    if (foundCategories.length === 0) return null;
    return { budget, categories: foundCategories };
  };

  const extractSingleItemAndBudget = (text) => {
    const patterns = [
      /(?:looking for|need|want|get|find|i want|i need)\s+(\w+[\s\w]*?)\s+(?:for|under|within|budget of?|price)\s+(\d{4,})/i,
      /(\w+[\s\w]*?)\s+(?:for|under|within)\s+(\d{4,})/i,
      /(?:i have|my budget is)\s+(\d{4,})\s+(?:and i need|i need|for a?)\s+(\w+[\s\w]*)/i,
      /(?:budget|price)\s+(\d{4,})\s+(?:for a?)?\s+(\w+[\s\w]*)/i,
    ];
    for (let pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        let item = match[1].trim().toLowerCase();
        let budget = parseFloat(match[2]);
        item = item.replace(/\s+(for|under|within)$/i, '');
        item = normalizeCategory(item);
        if (item.length > 2 && budget > 0) return { item, budget };
      }
    }
    return null;
  };

  const handleDropCommand = (userText, allProducts, lastContext, lastPlanData) => {
    if (!lastContext || !lastPlanData) return null;
    const lower = userText.toLowerCase();
    const dropPatterns = /(drop|remove|delete|cancel|take out|don't want|no more|actually.*not|skip)\s+(\w+[\s\w]*)/i;
    const match = userText.match(dropPatterns);
    if (!match) return null;
    
    let target = match[2].trim().toLowerCase();
    target = target.replace(/[?.,!]$/, '');
    let categoryToDrop = null;
    for (let prod of lastPlanData.affordableProducts) {
      if (prod.title && prod.title.toLowerCase().includes(target)) {
        categoryToDrop = prod.category;
        break;
      }
    }
    if (!categoryToDrop) {
      const foundCategory = lastContext.categories.find(cat => 
        cat.toLowerCase().includes(target) || target.includes(cat.toLowerCase())
      );
      if (foundCategory) categoryToDrop = foundCategory;
    }
    if (!categoryToDrop) return null;
    const newCategories = lastContext.categories.filter(c => c !== categoryToDrop);
    if (newCategories.length === 0) {
      return {
        text: `Alright, I've removed ${categoryToDrop}. Your shopping list is empty now. Want to start over with a new budget? Just tell me what you need! 😊`,
        products: null,
        newContext: null,
      };
    }
    const newPlan = suggestAffordableCombination(lastContext.budget, newCategories, allProducts);
    storeBudgetPlan(lastContext.budget, newCategories, newPlan);
    let responseText = `Got it! I've taken out the ${categoryToDrop}. 👍 Here's your updated plan with your Ksh ${lastContext.budget}:\n\n`;
    if (newPlan.affordableProducts.length === 0) {
      responseText = `Hmm, after dropping ${categoryToDrop}, your remaining items (${newCategories.join(', ')}) don't fit within Ksh ${lastContext.budget}. Want to adjust your budget or choose different items?`;
      return { text: responseText, products: null, newContext: { budget: lastContext.budget, categories: newCategories } };
    }
    const affordableList = newPlan.affordableProducts.map(p => `• ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
    responseText += `${affordableList}\n\n**Total: Ksh ${newPlan.totalUsed.toFixed(2)}**  |  Remaining: Ksh ${newPlan.remainingBudget.toFixed(2)}`;
    if (newPlan.missingCategories.length > 0) {
      responseText += `\n\nYou're still missing: ${newPlan.missingCategories.join(', ')}. Want me to suggest cheaper alternatives or drop something else?`;
    } else {
      responseText += `\n\n🎉 You got everything on your list! Feel free to ask for more details or check out the offers.`;
      if (newPlan.remainingBudget > 0) {
        responseText += `\n\n💡 You have Ksh ${newPlan.remainingBudget.toFixed(2)} left – would you like to add another item? Just say "add a [category]".`;
      }
    }
    return { text: responseText, products: newPlan.affordableProducts, newContext: { budget: lastContext.budget, categories: newCategories } };
  };

  const handleNegativeResponse = (userText) => {
    const lower = userText.trim().toLowerCase();
    if ((lower === 'no' || lower === 'nope' || lower === 'not now' || lower === 'don\'t add' || lower === 'no thanks') && pendingAddCategory) {
      const cat = pendingAddCategory;
      setPendingAddCategory(null);
      return {
        text: `No problem! I won't add "${cat}" to your plan. Is there anything else you'd like to add or remove? 😊`,
        products: null,
        clearPending: true,
      };
    }
    if ((lower === 'no' || lower === 'nope') && pendingAddToPlan) {
      const cat = pendingAddToPlan;
      setPendingAddToPlan(null);
      return {
        text: `Okay, I won't add "${cat}" to your plan. Your plan remains unchanged. Anything else I can help with?`,
        products: null,
        clearPending: true,
      };
    }
    return null;
  };

  const handleYesAddToPlan = async (userText, allProducts) => {
    const lower = userText.trim().toLowerCase();
    if ((lower === 'yes' || lower === 'yeah' || lower === 'yep' || lower === 'sure' || lower === 'ok' || lower === 'add it') && pendingAddToPlan && lastBudgetContext) {
      const categoryToAdd = pendingAddToPlan;
      setPendingAddToPlan(null);
      const newCategories = [...lastBudgetContext.categories, categoryToAdd];
      const plan = suggestAffordableCombination(lastBudgetContext.budget, newCategories, allProducts);
      storeBudgetPlan(lastBudgetContext.budget, newCategories, plan);
      let response = `👍 Added ${categoryToAdd} to your plan. `;
      if (plan.missingCategories.length === 0) {
        const productList = plan.affordableProducts.map(p => `✅ ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
        response += `With your Ksh ${lastBudgetContext.budget}, you can now get:\n${productList}\n**Total: Ksh ${plan.totalUsed.toFixed(2)}** – remaining: Ksh ${plan.remainingBudget.toFixed(2)}.`;
        if (plan.remainingBudget > 0) {
          response += `\n\n💡 You still have Ksh ${plan.remainingBudget.toFixed(2)} left – want to add something else?`;
        }
      } else {
        const affordableList = plan.affordableProducts.map(p => `✅ ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
        response += `You can afford:\n${affordableList}\n❌ Missing: ${plan.missingCategories.join(', ')}. You may need to drop something or increase budget.`;
      }
      return { text: response, products: plan.affordableProducts };
    }
    return null;
  };

  const handleCategorySearchWithoutBudget = async (searchTerm, allProducts) => {
    const matched = searchProductsByTerm(searchTerm, allProducts).slice(0, 6);
    if (matched.length === 0) {
      return {
        text: `🙁 Sorry, we don't have any "${searchTerm}" in stock right now. But we can custom‑make one for you! 📞 [Click here to chat on WhatsApp](${WHATSAPP_LINK}) – our team will help you design exactly what you need.`,
        products: null,
      };
    }
    setAwaitingBudgetForCategory(searchTerm);
    const sampleList = matched.map(p => `• ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
    return {
      text: `Sure! Here are some ${searchTerm} we have:\n${sampleList}\n\n💡 To help me narrow down the best options for you, could you tell me your budget? For example, "My budget is 50,000" or "I have 80,000".\n\nYou can also add any of these to your cart by saying "add [product name] to my cart".`,
      products: matched,
    };
  };

  const handleBudgetAfterCategory = async (budget, searchTerm, allProducts) => {
    const matched = searchProductsByTerm(searchTerm, allProducts);
    const affordable = matched.filter(p => getActualPrice(p) <= budget).slice(0, 8);
    if (affordable.length === 0) {
      const cheapest = matched.length > 0 ? matched.reduce((a, b) => getActualPrice(a) < getActualPrice(b) ? a : b) : null;
      const minPrice = cheapest ? getActualPrice(cheapest).toFixed(2) : 'N/A';
      return {
        text: `With Ksh ${budget}, I couldn't find any ${searchTerm} within your budget. ${minPrice !== 'N/A' ? `The cheapest ${searchTerm} we have is Ksh ${minPrice}.` : ''} Would you like to increase your budget or see other categories? Or we can custom‑make one – [click here to WhatsApp us](${WHATSAPP_LINK}).`,
        products: null,
      };
    }
    setAwaitingBudgetForCategory(null);
    const plan = suggestAffordableCombination(budget, [searchTerm], allProducts);
    storeBudgetPlan(budget, [searchTerm], plan);
    const productList = affordable.map(p => `• ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
    return {
      text: `Great! With Ksh ${budget}, here are ${searchTerm} that fit your budget:\n${productList}\n\nWould you like to add any of these to your plan or to your cart? Just say "add the [product name] to my cart".`,
      products: affordable,
    };
  };

  const handleBudgetIncrease = async (newBudget, allProducts, additionalCategories = []) => {
    let categories = [];
    if (lastBudgetContext && lastBudgetContext.categories.length > 0) {
      categories = [...lastBudgetContext.categories];
    }
    if (additionalCategories.length > 0) {
      categories = [...new Set([...categories, ...additionalCategories])];
    }
    if (categories.length === 0) {
      const sortedByPrice = [...allProducts].sort((a, b) => getActualPrice(a) - getActualPrice(b));
      const affordable = [];
      let remaining = newBudget;
      for (let p of sortedByPrice) {
        const price = getActualPrice(p);
        if (price <= remaining) {
          affordable.push(p);
          remaining -= price;
        }
        if (affordable.length >= 3) break;
      }
      if (affordable.length === 0) {
        return { text: `With Ksh ${newBudget}, we don't have any products that fit. Maybe try a higher budget? I'm here to help!`, products: null };
      }
      const productList = affordable.map(p => `• ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
      return {
        text: `Great budget! Since you didn't mention specific items, here are three popular picks within Ksh ${newBudget}:\n${productList}\n\nTotal: Ksh ${(newBudget - remaining).toFixed(2)} | Left: Ksh ${remaining.toFixed(2)}\n\nLet me know if you want to see beds, sofas, recliners, or something else.`,
        products: affordable,
      };
    }
    const plan = suggestAffordableCombination(newBudget, categories, allProducts);
    storeBudgetPlan(newBudget, categories, plan);
    let responseText = `💰 Okay, I've ${lastBudgetContext ? 'updated' : 'set'} your budget to Ksh ${newBudget}. ${additionalCategories.length > 0 ? `Added ${additionalCategories.join(', ')} to your list. ` : ''}\n\nYour shopping list: ${categories.join(', ')}\n\n`;
    if (plan.affordableProducts.length === 0) {
      const minProduct = allProducts.reduce((a, b) => getActualPrice(a) < getActualPrice(b) ? a : b, allProducts[0]);
      const minPrice = minProduct ? getActualPrice(minProduct).toFixed(2) : '???';
      responseText = `😔 With Ksh ${newBudget}, we can't afford any of your items (${categories.join(', ')}). The cheapest product we have is Ksh ${minPrice}. Maybe try a higher budget or different items?`;
      return { text: responseText, products: null };
    }
    const affordableList = plan.affordableProducts.map(p => `✅ ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
    responseText += `Here's what fits:\n${affordableList}\n\n**Total: Ksh ${plan.totalUsed.toFixed(2)}** | Remaining: Ksh ${plan.remainingBudget.toFixed(2)}`;
    if (plan.missingCategories.length > 0) {
      responseText += `\n\n❌ Still missing: ${plan.missingCategories.join(', ')}. You might need to drop something or increase a bit more.`;
    } else {
      responseText += `\n\n🎉 Perfect! You can get everything you wanted.`;
      if (plan.remainingBudget > 0) {
        responseText += `\n\n💡 You have Ksh ${plan.remainingBudget.toFixed(2)} left – would you like to add another item? Just say "add a [category]".`;
      }
    }
    return { text: responseText, products: plan.affordableProducts };
  };

  const handleDoYouHave = async (category, allProducts) => {
    const matched = searchProductsByTerm(category, allProducts).slice(0, 6);
    if (matched.length === 0) {
      return {
        text: `🙁 Sorry, we don't have any "${category}" at the moment. But we can custom‑make exactly what you're looking for! 📞 [Click here to chat on WhatsApp](${WHATSAPP_LINK}) – our team will help you design it.`,
        products: null,
      };
    }
    const sampleList = matched.map(p => `• ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
    let extra = '';
    if (lastBudgetContext) {
      extra = `\n\n💡 You currently have a budget plan (Ksh ${lastBudgetContext.budget}) for ${lastBudgetContext.categories.join(', ')}. Would you like to add ${category} to that plan? Just say "yes" or "add it".`;
      setPendingAddToPlan(category);
    } else {
      extra = `\n\n💡 If you'd like to see these within a budget, just tell me your budget (e.g., "I have 50,000"). You can also add them directly to your cart by saying "add [product name] to my cart".`;
    }
    return {
      text: `Yes, we have ${category}! Here are some examples:\n${sampleList}${extra}`,
      products: matched,
    };
  };

  const formatMessageWithWhatsAppLink = (text) => {
    if (!text) return text;
    const regex = new RegExp(`(📞\\s*)?(\\+?${WHATSAPP_NUMBER.replace(/\+/g, '\\+')})`, 'g');
    const markdownLinkRegex = /\[(.*?)\]\((.*?)\)/g;
    let processed = text.replace(markdownLinkRegex, (match, label, url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#25D366;font-weight:bold">${label}</a>`;
    });
    const parts = processed.split(regex);
    return parts.map((part, i) => {
      if (part === WHATSAPP_NUMBER || part === `+${WHATSAPP_NUMBER.replace(/\+/g, '')}`) {
        return <a key={i} href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" style={{ color: '#25D366', fontWeight: 'bold' }}>{part}</a>;
      }
      if (part && part.includes('📞')) {
        return <span key={i}>{part}</span>;
      }
      return <span key={i} dangerouslySetInnerHTML={{ __html: part }} />;
    });
  };

  const handleUserMessage = async (userText) => {
    const lower = userText.toLowerCase();
    const allProducts = await fetchAllProducts();

    if (orderStep === 'collecting_details' && currentDetailField) {
      const result = processDeliveryInput(
        userText, 
        deliveryDetails, 
        cartItems, 
        setDeliveryDetails, 
        setCurrentDetailField, 
        setOrderStep, 
        setCartItems, 
        getCartTotal, 
        getActualPrice, 
        WHATSAPP_NUMBER.replace('+', '')
      );
      if (result) {
        if (typeof result === 'string') {
          return { text: result, products: null, component: null };
        } else {
          return { text: result.text, products: null, component: null };
        }
      }
    }

    const intentResult = await handleIntent(lower, allProducts);
    if (intentResult) {
      setShowSuggestions(false);
      return { text: intentResult.response, products: intentResult.products, component: intentResult.component };
    }

    const negativeResp = handleNegativeResponse(userText);
    if (negativeResp) return negativeResp;

    const yesAddResp = await handleYesAddToPlan(userText, allProducts);
    if (yesAddResp) return yesAddResp;

    if (lastBudgetContext && lastPlan) {
      const dropResp = handleDropCommand(userText, allProducts, lastBudgetContext, lastPlan);
      if (dropResp) {
        if (dropResp.newContext) setLastBudgetContext(dropResp.newContext);
        return { text: dropResp.text, products: dropResp.products };
      }
    }

    const multiCategoryPlan = extractBudgetAndMultipleCategories(userText);
    if (multiCategoryPlan && multiCategoryPlan.categories.length > 0) {
      setShowSuggestions(false);
      const { budget, categories } = multiCategoryPlan;
      const plan = suggestAffordableCombination(budget, categories, allProducts);
      storeBudgetPlan(budget, categories, plan);
      if (plan.affordableProducts.length === 0) {
        const cheapestAny = allProducts.reduce((a, b) => getActualPrice(a) < getActualPrice(b) ? a : b, allProducts[0]);
        const minPrice = cheapestAny ? getActualPrice(cheapestAny).toFixed(2) : '???';
        return {
          text: `😔 With Ksh ${budget}, we can't afford any of the items you listed (${categories.join(', ')}). The cheapest thing we sell is Ksh ${minPrice}. Could you increase your budget a bit? Or pick different items?`,
          products: null,
        };
      }
      let responseText = `💰 With your budget of Ksh ${budget}, you're looking for ${categories.join(', ')}.\n\n`;
      if (plan.missingCategories.length === 0) {
        const productList = plan.affordableProducts.map(p => `✅ ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
        responseText += `You can get everything you asked for:\n${productList}\n\n**Total: Ksh ${plan.totalUsed.toFixed(2)}** – you'll have Ksh ${plan.remainingBudget.toFixed(2)} left over.`;
        if (plan.remainingBudget > 0) {
          responseText += `\n\n💡 Want to add another item? Just say "add a [category]".`;
        }
      } else {
        const affordableList = plan.affordableProducts.map(p => `✅ ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
        responseText += `You can afford ${plan.affordableProducts.length} out of ${categories.length} items:\n${affordableList}\n\n`;
        responseText += `❌ Missing: ${plan.missingCategories.join(', ')}. `;
        const missingWithPrice = plan.missingWithPrice;
        if (missingWithPrice.length > 0) {
          const firstMissing = missingWithPrice[0];
          responseText += `The remaining budget (Ksh ${plan.remainingBudget.toFixed(2)}) isn't enough for a ${firstMissing.category}. The cheapest one is Ksh ${firstMissing.price.toFixed(2)}.\n\n💡 If you increase your budget by Ksh ${(firstMissing.price - plan.remainingBudget).toFixed(2)} to Ksh ${(budget + (firstMissing.price - plan.remainingBudget)).toFixed(2)}, you can include it. Or we can drop something else – just say "drop the ${firstMissing.category}".`;
        } else {
          responseText += `We don't have those categories in stock – feel free to ask for something else!`;
        }
      }
      return { text: responseText, products: plan.affordableProducts };
    }

    const addPattern = /(?:add|also need|include|plus|and a?)\s+(\w+[\s\w]*)/i;
    const addMatch = userText.match(addPattern);
    if (addMatch && lastBudgetContext && !lower.match(/\d{4,}/)) {
      setShowSuggestions(false);
      let categoryToAdd = addMatch[1].trim().toLowerCase();
      categoryToAdd = normalizeCategory(categoryToAdd.replace(/[?.,!]$/, ''));
      if (!lastBudgetContext.categories.includes(categoryToAdd)) {
        const newCategories = [...lastBudgetContext.categories, categoryToAdd];
        const plan = suggestAffordableCombination(lastBudgetContext.budget, newCategories, allProducts);
        storeBudgetPlan(lastBudgetContext.budget, newCategories, plan);
        let response = `👍 Alright, I've added ${categoryToAdd} to your shopping list. Now you're looking for: ${newCategories.join(', ')}.\n\n`;
        if (plan.affordableProducts.length === 0) {
          response += `😕 With your current budget of Ksh ${lastBudgetContext.budget}, we can't afford any of these. Maybe increase your budget or drop some items?`;
          return { text: response, products: null };
        }
        const affordableList = plan.affordableProducts.map(p => `✅ ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
        response += `Here's what fits within Ksh ${lastBudgetContext.budget}:\n${affordableList}`;
        if (plan.missingCategories.length > 0) {
          response += `\n\n❌ Still missing: ${plan.missingCategories.join(', ')}. You could increase budget or drop an item.`;
        } else {
          response += `\n\n🎉 You got everything!`;
          if (plan.remainingBudget > 0) {
            response += `\n\n💡 You have Ksh ${plan.remainingBudget.toFixed(2)} left – want to add another item? Just say "add a [category]".`;
          }
        }
        return { text: response, products: plan.affordableProducts };
      } else {
        return { text: `You already have ${categoryToAdd} in your plan. Want to remove it instead?`, products: null };
      }
    }

    const budgetIncreaseMatch = lower.match(/(?:increased|new|changed|updated|now have|i have)\s+(\d{4,})/i);
    if (budgetIncreaseMatch && lastBudgetContext && lastBudgetContext.categories.length > 0 && !lower.includes('add') && !lower.includes('need')) {
      setShowSuggestions(false);
      const newBudget = parseFloat(budgetIncreaseMatch[1]);
      const updateResponse = await handleBudgetIncrease(newBudget, allProducts, []);
      if (updateResponse) return updateResponse;
    }

    if (awaitingBudgetForCategory) {
      const budgetMatch = lower.match(/(?:budget\s*)?(\d{4,})/);
      if (budgetMatch) {
        const budget = parseFloat(budgetMatch[1]);
        const commonCategories = ['bed', 'sofa', 'table', 'recliner', 'chair', 'desk', 'cabinet', 'tv stand', 'wardrobe', 'dining table', 'coffee table', 'lamp'];
        let newCategory = null;
        for (let cat of commonCategories) {
          if (lower.includes(cat) && cat !== awaitingBudgetForCategory) {
            newCategory = cat;
            break;
          }
        }
        if (newCategory) {
          setAwaitingBudgetForCategory(null);
          return await handleBudgetAfterCategory(budget, newCategory, allProducts);
        } else {
          return await handleBudgetAfterCategory(budget, awaitingBudgetForCategory, allProducts);
        }
      }
    }

    const singleItem = extractSingleItemAndBudget(userText);
    if (singleItem) {
      setShowSuggestions(false);
      const { item, budget } = singleItem;
      setAwaitingBudgetForCategory(null);
      const matched = searchProductsByTerm(item, allProducts);
      const affordable = matched.filter(p => getActualPrice(p) <= budget).slice(0, 8);
      if (affordable.length === 0) {
        if (matched.length === 0) {
          return {
            text: `🙁 Sorry, I couldn't find any "${item}" in our catalog. But we can custom‑make one! 📞 [Click here to WhatsApp us](${WHATSAPP_LINK}) for a custom order.`,
            products: null,
          };
        }
        const cheapest = matched.reduce((a, b) => getActualPrice(a) < getActualPrice(b) ? a : b);
        return {
          text: `With Ksh ${budget}, I couldn't find any ${item} within your budget. The cheapest ${item} we have is Ksh ${getActualPrice(cheapest).toFixed(2)}. Would you like to increase your budget?`,
          products: null,
        };
      }
      const plan = suggestAffordableCombination(budget, [item], allProducts);
      storeBudgetPlan(budget, [item], plan);
      const productList = affordable.map(p => `• ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
      return {
        text: `Great! With Ksh ${budget}, here are ${item}s that fit your budget:\n${productList}\n\nI've set this as your current plan. Want to add other items or add any to your cart?`,
        products: affordable,
      };
    }

    const commonSearchTerms = ['bed', 'sofa', 'table', 'chair', 'desk', 'cabinet', 'tv stand', 'wardrobe', 'dining', 'lamp', 'coffee table', 'recliner', 'couch', 'mattress'];
    let detectedTerm = null;
    for (let term of commonSearchTerms) {
      if (lower.includes(term) && !lower.match(/\d{4,}/)) {
        detectedTerm = normalizeCategory(term);
        break;
      }
    }
    if (detectedTerm && !lower.match(/\d{4,}/)) {
      return await handleCategorySearchWithoutBudget(detectedTerm, allProducts);
    }

    const doYouHaveMatch = lower.match(/(?:do you have|is there|got any|what about)\s+(\w+[\s\w]*)/i);
    if (doYouHaveMatch) {
      let category = doYouHaveMatch[1].trim().replace(/[?.,!]$/, '');
      if (category.length > 2) {
        return await handleDoYouHave(category, allProducts);
      }
    }

    let searchTerm = '';
    const stopWords = ['show', 'me', 'find', 'search', 'for', 'the', 'a', 'an', 'i', 'want', 'need', 'please', 'can', 'you', 'get', 'have', 'budget', 'with', 'what', 'about', 'and', 'or', 'do', 'does', 'is', 'are', 'there', 'look', 'looking'];
    const words = userText.split(' ');
    for (let word of words) {
      if (word.length > 2 && !stopWords.includes(word.toLowerCase()) && isNaN(word)) {
        searchTerm = word;
        break;
      }
    }
    if (searchTerm) {
      const matched = searchProductsByTerm(searchTerm, allProducts).slice(0, 6);
      if (matched.length === 0) {
        return {
          text: `🙁 Hmm, I can't find any "${searchTerm}" in our catalog. But we can custom‑make it! 📞 [Click here to chat on WhatsApp](${WHATSAPP_LINK}) – our team will bring your idea to life.`,
          products: null,
        };
      }
      if (!detectedTerm) {
        setAwaitingBudgetForCategory(searchTerm);
        const sampleList = matched.map(p => `• ${p.title} – Ksh ${getActualPrice(p).toFixed(2)}`).join('\n');
        return {
          text: `I found these for "${searchTerm}":\n${sampleList}\n\n💡 To help you find the perfect match, could you share your budget? Just say "My budget is X" or "I have X".\n\nYou can also add any to your cart by saying "add [product name] to my cart".`,
          products: matched,
        };
      }
      let extraText = '';
      if (lastBudgetContext && !lastBudgetContext.categories.includes(searchTerm)) {
        extraText = `\n\n💡 You have a budget plan (Ksh ${lastBudgetContext.budget}) for ${lastBudgetContext.categories.join(', ')}. Would you like to add "${searchTerm}"? Just say "add ${searchTerm}".`;
        setPendingAddCategory(searchTerm);
      }
      return {
        text: `I found ${matched.length} item(s) for "${searchTerm}".${extraText}`,
        products: matched,
      };
    }

    return {
      text: "I'm not sure I understood that – but don't worry! 😊 You can ask me things like:\n• 'I have 180,000 and need a bed, sofa, and dining table'\n• 'Show me sofas under 50000'\n• 'Add The Dollar Furniture Bed to my cart'\n• 'Show my cart'\n• 'Place order'\n• 'What's your return policy?'\n\nJust tell me what you want in plain English!",
      products: null,
    };
  };

  const sendMessage = async (textOverride = null) => {
    const messageText = textOverride || inputValue.trim();
    if (!messageText) return;

    setShowSuggestions(false);

    const userMsg = {
      id: Date.now(),
      text: messageText,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      products: null,
      component: null,
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    const aiResponse = await handleUserMessage(messageText);
    setIsTyping(false);

    const aiMsg = {
      id: Date.now() + 1,
      text: aiResponse.text,
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      products: aiResponse.products || null,
      component: aiResponse.component || null,
    };
    setMessages(prev => [...prev, aiMsg]);
    
    // Always speak the AI response (if it's a reasonable length)
    if (aiResponse.text && aiResponse.text.length > 10) {
      speakText(aiResponse.text);
    }
  };

  const handleSuggestionClick = (suggestionValue) => {
    setInputValue(suggestionValue);
    sendMessage(suggestionValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const replayMessage = (text) => {
    if (text && text.length > 10) {
      speakText(text);
    } else {
      message.info("Message too short to speak.");
    }
  };

  const ProductGrid = ({ products }) => {
    if (!products || products.length === 0) return null;
    return (
      <div style={{ marginTop: 12, width: '100%' }}>
        <Row gutter={[12, 12]} justify="start">
          {products.map(product => {
            const discounted = getActualPrice(product);
            return (
              <Col xs={12} sm={8} md={6} key={product.id}>
                <Card
                  hoverable
                  size="small"
                  style={{ borderRadius: 12, overflow: 'hidden' }}
                  cover={
                    <img
                      alt={product.title}
                      src={product.image}
                      style={{ height: 120, objectFit: 'cover' }}
                      onClick={() => navigate(`/product-details/${product.id}`)}
                    />
                  }
                  onClick={() => navigate(`/product-details/${product.id}`)}
                >
                  <Card.Meta
                    title={
                      <div style={{ fontSize: 12, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <TagOutlined style={{ color: 'orangered' }} />
                        <span>{product.title?.slice(0, 30)}</span>
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                          <span style={{ textDecoration: 'line-through', fontSize: 11, color: '#999' }}>
                            Ksh {product.price}
                          </span>
                          <span style={{ fontWeight: 'bold', color: '#ff5722', fontSize: 13 }}>
                            Ksh {discounted.toFixed(2)}
                          </span>
                          {product.discount > 0 && (
                            <Tag icon={<PercentageOutlined />} color="green" style={{ fontSize: 10, margin: 0 }}>
                              {product.discount}% OFF
                            </Tag>
                          )}
                        </div>
                        <Button
                          size="small"
                          icon={<ShoppingCartOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product, 1);
                          }}
                          style={{ width: '100%' }}
                        >
                          Add to Cart
                        </Button>
                      </div>
                    }
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
        {products.length >= 6 && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            Showing first 6 results. Visit the products page to see all.
          </Text>
        )}
      </div>
    );
  };

  const CartSummary = () => {
    if (cartItems.length === 0) return null;
    return (
      <div style={{ marginTop: 12, padding: 12, background: '#f0f2f5', borderRadius: 12 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🛒 Your Cart</div>
        {cartItems.map(item => (
          <div key={item.product.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span>{item.product.title} × {item.quantity}</span>
            <span>Ksh {(getActualPrice(item.product) * item.quantity).toFixed(2)}</span>
            <Button size="small" icon={<DeleteOutlined />} onClick={() => removeFromCart(item.product.id)} danger />
          </div>
        ))}
        <div style={{ fontWeight: 'bold', borderTop: '1px solid #ddd', paddingTop: 8, marginTop: 8 }}>
          Total: Ksh {getCartTotal().toFixed(2)}
        </div>
        <Button type="primary" size="small" onClick={() => sendMessage('place order')} style={{ marginTop: 8 }} block>
          Place Order
        </Button>
      </div>
    );
  };

  const AIImage = () => (
    <Avatar
      src="https://firebasestorage.googleapis.com/v0/b/dollar-eda6d.firebasestorage.app/o/dollar%2FChatGPT%20Image%20May%207%2C%202026%2C%2012_04_20%20AM.png?alt=media&token=464a90c8-9b9c-449d-99e7-a64cec245f9f"
      size="small"
      style={{ backgroundColor: 'transparent' }}
    />
  );

  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: '#fff', 
        padding: '12px 20px', 
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} type="text" />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src="https://firebasestorage.googleapis.com/v0/b/dollar-eda6d.firebasestorage.app/o/dollar%2FChatGPT%20Image%20May%207%2C%202026%2C%2012_04_20%20AM.png?alt=media&token=464a90c8-9b9c-449d-99e7-a64cec245f9f"
              alt="Dollar Furniture AI"
              style={{ width: 32, borderRadius: '50%' }}
            />
            <div>
              <Title level={4} style={{ margin: 0, fontSize: '18px' }}>Dollar Furniture AI</Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>Online • Voice Enabled • Shop & Budgeting Assistant</Text>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShoppingCartOutlined style={{ fontSize: 16 }} />
          <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{cartItems.length}</Text>
        </div>
      </div>

      {/* Chat Messages */}
      <div
        ref={chatContainerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {messages.map((msg) => (
          <div key={msg.id}>
            <div style={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start', marginBottom: 4 }}>
              {msg.sender === 'ai' && <AIImage />}
              <div
                style={{
                  maxWidth: '85%',
                  padding: '10px 16px',
                  borderRadius: 20,
                  backgroundColor: msg.sender === 'user' ? '#1890ff' : '#fff',
                  color: msg.sender === 'user' ? 'white' : '#333',
                  fontSize: 14,
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                  marginLeft: msg.sender === 'ai' ? 8 : 0,
                  marginRight: msg.sender === 'user' ? 8 : 0,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {msg.sender === 'ai' 
                    ? formatMessageWithWhatsAppLink(msg.text)
                    : msg.text
                  }
                </div>
                <div style={{ fontSize: 10, marginTop: 4, textAlign: 'right', opacity: 0.7, color: msg.sender === 'user' ? '#e6f7ff' : '#888' }}>
                  {msg.timestamp}
                  {msg.sender === 'ai' && (
                    <Button
                      type="text"
                      size="small"
                      icon={<SoundOutlined />}
                      onClick={() => replayMessage(msg.text)}
                      style={{ marginLeft: 8, fontSize: 12, padding: '0 4px' }}
                    />
                  )}
                </div>
                {msg.products && <ProductGrid products={msg.products} />}
                {msg.component && <div style={{ marginTop: 12 }}>{msg.component}</div>}
              </div>
              {msg.sender === 'user' && (
                <Avatar style={{ backgroundColor: '#87d068', marginLeft: 8 }} size="small">U</Avatar>
              )}
            </div>
          </div>
        ))}
        {isTyping && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 40 }}>
            <AIImage />
            <div style={{ backgroundColor: '#fff', padding: '10px 16px', borderRadius: 20, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              <span>Dollar Furniture AI is thinking</span><span> 🤔</span>
            </div>
          </div>
        )}
        {loadingProducts && (
          <div style={{ textAlign: 'center', margin: '8px 0' }}>
            <Spin size="small" /> <Text type="secondary">Fetching products...</Text>
          </div>
        )}
        <CartSummary />
      </div>

      {/* Suggestions Chips */}
      {showSuggestions && (
        <div style={{
          backgroundColor: '#fff',
          borderTop: '1px solid #f0f0f0',
          padding: '12px 16px',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          display: 'flex',
          gap: '8px',
          scrollbarWidth: 'thin'
        }}>
          {suggestions.map((suggestion, idx) => (
            <Button
              key={idx}
              size="small"
              style={{
                borderRadius: 20,
                backgroundColor: '#f5f5f5',
                border: 'none',
                fontSize: 13,
              }}
              onClick={() => handleSuggestionClick(suggestion.value)}
            >
              {suggestion.text}
            </Button>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div style={{ 
        backgroundColor: '#fff',
        borderTop: '1px solid #e0e0e0',
        padding: '12px 16px',
        flexShrink: 0,
        paddingBottom: 'env(safe-area-inset-bottom, 12px)'
      }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type or click the mic to speak... 😊"
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{ borderRadius: 40, resize: 'none' }}
          />
          <Button
            icon={isListening ? <AudioOutlined style={{ color: '#ff4d4f' }} /> : <AudioOutlined />}
            onClick={startListening}
            style={{ borderRadius: 40, marginLeft: 4 }}
            type={isListening ? "primary" : "default"}
            danger={isListening}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => sendMessage()}
            disabled={!inputValue.trim()}
            style={{ borderRadius: 40, marginLeft: 4 }}
          >
            Send
          </Button>
        </Space.Compact>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Need a custom design? <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" style={{ color: '#25D366' }}>Chat on WhatsApp</a>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;